import React, { useRef, useState } from 'react';
import { bool, func, object, array } from 'prop-types';
import { Button } from 'antd-v5';

import Crumbs from './Crumbs';
import Errors from './Errors';
import { updateData } from './utils';
import { isValidDate } from './Field/props';

// One @, something either side, a dot in the domain, no whitespace.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normaliseEmail = value => `${value || ''}`.trim().toLowerCase();

export const FIELD_STYLES = {
  width: 506,
  maxWidth: '100%',
  minWidth: 'auto',
};
export const HEADING_CLASSES = 'fs-16 fw-600 m-top-0 m-btm-8';

const Wizard = ({ data, setData, onSubmit, showAll, steps, isSola }) => {
  const [disableContinue, setDisableContinue] = useState(false);
  const [errors, setErrors] = useState([]);
  const wizardForm = useRef(null);

  // changing steps/pages
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const finalStepIndex = steps.length - 1;

  const previous = () => setActiveStepIndex(Math.max(activeStepIndex - 1, 0));
  const next = () => setActiveStepIndex(Math.min(activeStepIndex + 1, finalStepIndex));

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.getElementById('scrollWatchId').scrollTo(0, 0);
  };

  // helper to show step-level errors
  const pushError = error => {
    setErrors(errorList => [...errorList, error].filter((value, index, self) => self.indexOf(value) === index));
  };

  const validateField = field => {
    const name = field.label || field.id;
    const value = `${field.value || ''}`;
    const isNumeric = field.inputMode === 'numeric';
    const unit = isNumeric ? 'digits' : 'characters';

    if (field.hasError) {
      pushError(field.errorMessage);
      return true;
    }

    if (Boolean(field.optional) && !value) {
      return false;
    }

    if (!value) {
      pushError(`${name} is required`);
      return true;
    }

    // Digits only: Number() would accept '1e5' or ' 12 ' for an account number.
    if (isNumeric && !/^\d+$/.test(value)) {
      pushError(`${name} must contain only digits`);
      return true;
    }

    // The blur validator (validateDateField) only colours the input; this is what stops the
    // submit. Same rule, so the two never disagree.
    if (field.format === 'date' && !isValidDate(value)) {
      pushError(`${name} must be a valid date in MM/DD/YYYY format`);
      return true;
    }

    if (field.type === 'email' && !EMAIL_PATTERN.test(value.trim())) {
      pushError(`${name} must be a valid email address`);
      return true;
    }

    const minLength = Number(field.minLength);
    const maxLength = Number(field.maxLength);

    if (field.minLength && !Number.isNaN(minLength) && value.length < minLength) {
      pushError(
        field.maxLength && minLength !== maxLength
          ? `${name} must be between ${minLength} and ${maxLength} ${unit}`
          : `${name} must be ${minLength} ${unit}`,
      );
      return true;
    }

    if (field.maxLength && !Number.isNaN(maxLength) && value.length > maxLength) {
      pushError(`${name} must be no more than ${maxLength} ${unit}`);
      return true;
    }

    return false;
  };

  // helper to validate active fields
  const validateFields = activeFields => {
    const errorFields = activeFields.filter(el => {
      return validateField(el);
    });

    // Cross-field: the confirmation must match the email it confirms. Both only exist on the
    // Sola business step; anywhere else one is absent and this is skipped.
    const email = activeFields.find(({ id }) => id === 'businessEmail');
    const confirm = activeFields.find(({ id }) => id === 'businessEmailConfirm');

    if (
      email &&
      confirm &&
      email.value &&
      confirm.value &&
      normaliseEmail(email.value) !== normaliseEmail(confirm.value)
    ) {
      pushError('Business Email and Confirm Business Email must match');

      if (!errorFields.includes(confirm)) {
        errorFields.push(confirm);
      }
    }

    return errorFields;
  };

  // form submission
  const isSubmitButton = showAll || activeStepIndex === finalStepIndex;

  // helper to get values from fields
  const getValues = (fields, isSaving) =>
    fields.reduce((obj, { id, value, forget }) => {
      return {
        ...obj,
        [id]: isSaving && forget ? null : value,
      };
    }, {});

  // get fields
  const getFields = () => {
    const fields = Array.from(wizardForm.current.elements)
      .filter(({ id }) => id)
      .map(el => {
        // The visible label, for error messages that read as English rather than as ids. Read
        // from the input's own Field wrapper rather than el.labels, so a label bound to another
        // element with the same id (showAll renders every step at once) can never be picked up.
        const ownLabel = el.closest('.field')?.querySelector('label');
        const label = ownLabel || (el.labels && el.labels[0]);

        return {
          id: el.id,
          bluesnap: el.id,
          label: label ? label.textContent.trim() : el.id,
          value: el.value,
          active: el.getAttribute('data-active') === 'true',
          optional: el.getAttribute('data-optional') === 'true',
          forget: el.getAttribute('data-forget') === 'true',
          minLength: el.getAttribute('minlength'),
          maxLength: el.getAttribute('maxlength'),
          inputMode: el.getAttribute('inputmode'),
          type: el.getAttribute('type'),
          // 'date' for MM/DD/YYYY inputs (see FIELD_PROPS.DATE).
          format: el.getAttribute('data-format'),
          hasError: el.getAttribute('data-error') === 'true',
          errorMessage: el.getAttribute('data-error-message'),
        };
      });

    return fields;
  };

  // get active fields, for validation & updating data
  const getActiveFields = () => {
    const fields = getFields();
    const activeFields = fields.filter(({ active }) => active);

    return activeFields;
  };

  // handler to update values by saving in the parent component
  // use instead of setData for changes that cause re-render
  const updateValues = (optionalValues = {}) => {
    const allFields = getFields();
    const allFieldsValues = getValues(allFields);

    updateData({ ...allFieldsValues, ...optionalValues }, setData);
  };

  // common actions between next/prev step changes
  const handleStepChange = () => {
    // save values so we don't lose them on re-render
    updateValues();

    // reset the page
    setErrors([]);
    setDisableContinue(false);
    scrollToTop();
  };

  // main handler for next/submit
  const handleSubmit = evt => {
    evt.preventDefault();

    handleStepChange();

    // validation
    const activeFields = getActiveFields();
    const errorFields = validateFields(activeFields);
    const isValid = !errorFields.length;

    // update our main `data` object with errors
    setData(d =>
      errorFields.reduce(
        (obj, { id }) => ({
          ...obj,
          [id]: { ...d[id], hasError: true },
        }),
        d,
      ),
    );

    // don't submit if invalid
    if (!isValid) {
      return;
    }

    if (isSubmitButton) {
      onSubmit(data, evt.target);
    } else {
      next();
    }
  };

  return (
    <>
      {/* breadcrumbs for steps/pages */}
      {!showAll && (
        <Crumbs
          crumbs={steps.map(({ title, to }, index) => ({
            title,
            isActive: activeStepIndex === index,
          }))}
          changeStep={index => {
            handleStepChange();
            setActiveStepIndex(index);
          }}
          className="m-btm-24"
        />
      )}

      <Errors errors={errors} />

      <form
        id="wizard-form"
        onSubmit={handleSubmit}
        // The browser's own validation would otherwise pre-empt ours: minLength/maxLength (which
        // getFields reads from the DOM) make it show its own tooltip and never fire onSubmit, so
        // our messages (by label, in "digits") never appear -- and inactive steps are display:none,
        // which the browser cannot tooltip at all.
        noValidate
        ref={wizardForm}
      >
        {steps.map(({ title, Component, helpText }, index) => {
          const active = showAll || activeStepIndex === index;

          return (
            <section
              key={title}
              /*
               * BlueSnap javascript requires that all inputs are found in the DOM,
               * so we hide them instead
               */
              style={{
                ...FIELD_STYLES,
                display: active ? 'block' : 'none',
              }}
            >
              {helpText ? (
                <span className="fs-12 fw-500 italic m-btm-28  d-inline-block">
                  All stores will use this account by default unless you explicitly assign the store to another account.
                </span>
              ) : null}
              <h2 className={HEADING_CLASSES}>{title}</h2>
              <Component
                data={data}
                setData={setData}
                updateValues={updateValues}
                active={active}
                // Steps render Sola-only fields from this.
                isSola={isSola}
                setDisableContinue={disabled => (active ? setDisableContinue(disabled) : null)}
              />
            </section>
          );
        })}

        <div className="buttons flex-row-center" style={{ ...FIELD_STYLES, gap: '12px' }}>
          {activeStepIndex > 0 && (
            <Button
              size="large"
              onClick={() => {
                handleStepChange();
                previous();
              }}
            >
              Go Back
            </Button>
          )}
          <Button type="primary" size="large" htmlType="submit" disabled={disableContinue}>
            {isSubmitButton ? 'Complete' : 'Continue'}
          </Button>
        </div>
      </form>
    </>
  );
};

Wizard.propTypes = {
  initialValues: object,
  onSubmit: func,
  showAll: bool,
  steps: array,
  isSola: bool,
};

Wizard.defaultProps = {
  initialValues: {},
  isSola: false,
};
export default Wizard;
